import { useState } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalCloseButton,
   Badge,
   Avatar,
   Box,
} from '@chakra-ui/react';
import { useGuides, isArchived, ArchivedGuideBadge } from '~/pages/_shared';
import { NullableRecord } from '@dozuki/web-js/utils';
import { GuideDifficulty } from '~/pages/api/courses/_shared/course-form/course-stage-editor/types';
import {
   Catalog,
   CatalogItem,
   CatalogItemContent,
   CatalogItemHeader,
   SearchForm,
} from '~/ui-components';
import { useLocalization } from '~/framework';
import { useDebounced } from '@dozuki/web-js/components';
import { useStageEditor } from '../StageEditor';
import { GuideBlurb } from '~/pages/api/guides/getGuides.api';

export function GuideSearchModal() {
   const { _js } = useLocalization();

   const stageEditor = useStageEditor();

   const [error, setError] = useState('');
   const [searchTerm, setSearchTerm] = useState('');
   const debouncedSearchTerm = useDebounced(searchTerm, 500);
   const { data: guides } = useGuides(debouncedSearchTerm);

   const guideDifficulty: NullableRecord<GuideDifficulty, string> = {
      Easy: _js('Easy'),
      Moderate: _js('Moderate'),
      Difficult: _js('Difficult'),
   };

   const guideDifficultyColors: NullableRecord<GuideDifficulty, string> = {
      Easy: 'green',
      Moderate: 'yellow',
      Difficult: 'red',
   };

   function handleClose() {
      stageEditor.closeOpenModal();
      setSearchTerm('');
      setError('');
   }

   function handleGuideSelected(guide: GuideBlurb) {
      if (isArchived(guide)) {
         setError(_js('Archived documents cannot be added to a course.'));
      } else {
         stageEditor.addStage({
            doctype: 'guide',
            docid: guide.guideid,
            frequency_unit: null,
            use_datacapture: null,
            frequency_quantity: null,
            strictness: null,
            docSummaryInfo: {
               title: guide.title,
               standardImage: guide.image?.thumbnail,
               releaseTitle: guide.releaseTitle,
               public: guide.public,
               attachedQuiz: guide.attachedQuiz,
            },
         });
         setSearchTerm('');
         setError('');
      }
   }

   return (
      <Modal isOpen={stageEditor.isGuideSearchModalOpen} onClose={handleClose} size="xl">
         <ModalOverlay />
         <ModalContent>
            <ModalHeader>{_js('Choose a Guide')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               {error && (
                  <Box backgroundColor={'red.100'} p={2} my={2} borderRadius={4}>
                     {error}
                  </Box>
               )}
               <SearchForm
                  name="guide-search"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder={_js('Search for a Guide')}
               />
               <Catalog>
                  {guides?.map(guide => (
                     <CatalogItem key={guide.guideid} onClick={() => handleGuideSelected(guide)}>
                        <Avatar src={guide.image?.thumbnail} />
                        <CatalogItemContent>
                           <CatalogItemHeader>{guide.title}</CatalogItemHeader>
                           <Badge ml="1" colorScheme={guideDifficultyColors[guide.difficulty]}>
                              {guideDifficulty[guide.difficulty]}
                           </Badge>
                           {isArchived(guide) && <ArchivedGuideBadge ml="1" />}
                        </CatalogItemContent>
                     </CatalogItem>
                  ))}
               </Catalog>
            </ModalBody>
         </ModalContent>
      </Modal>
   );
}
